import sortBy from "lodash/sortBy";
import type { ProductResponse } from "../../admin/products/productsApi";
import type { Currency } from "../../data/BaseEntity";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import type { Price } from "../../misc/price";
import { getSelectedProductPrice } from "../../sharedComponents/products/price/useProductsTotalPrice";
import type { CalculationProduct } from "../calculationTypes";
import type { PricesByDatesProductsAndOptions } from "../calculationVersionEdit/useProductsSellingPrices";
import { useProductsSellingPrices } from "../calculationVersionEdit/useProductsSellingPrices";
import { getAlternativeProducts, getDoubleOrTwinOption } from "./getAlternativeProducts";

export interface ProductAlternative {
  readonly product: CalculationProduct;
  readonly doubleTwinPrice: Price;
}

export interface UseCalculationProductAlternativesResult {
  readonly alternatives: ProductAlternative[];
  readonly selectedProductDoubleTwinPrice: Price | null;
}

function getSelectedDoubleTwinPrice(
  selectedProduct: CalculationProduct,
  products: Pick<ProductResponse, "id" | "options" | "currency">[],
  prices: PricesByDatesProductsAndOptions,
  calculationCurrency: Currency,
  margin: number,
): Price | null {
  const details = products.find((p) => p.id === selectedProduct.id);
  const doubleTwin = details ? getDoubleOrTwinOption(details) : null;
  if (!doubleTwin) {
    return null;
  }
  const productWithDoubleTwin = {
    ...selectedProduct,
    options: [{ id: doubleTwin.id, quantity: 1 }],
    currency: details?.currency,
  };
  const price = getSelectedProductPrice(
    productWithDoubleTwin,
    calculationCurrency,
    margin,
    1,
    0,
    (prices[selectedProduct.date] ?? {})[selectedProduct.id] ?? {},
  );
  return price.isPresent() ? price : null;
}

function getAlternativesWithDoubleTwinPrice(
  alternatives: CalculationProduct[],
  calculationCurrency: Currency,
  calculationMargin: number,
  products: Pick<ProductResponse, "id" | "name" | "options" | "productsGroup" | "currency">[],
  prices: PricesByDatesProductsAndOptions,
): ProductAlternative[] {
  return sortBy(
    alternatives
      .map((product) => {
        const withCurrency = { ...product, currency: products.find((p) => p.id === product.id)?.currency };
        const doubleTwinPrice = getSelectedProductPrice(
          withCurrency,
          calculationCurrency,
          calculationMargin,
          1,
          0,
          (prices[product.date] ?? {})[product.id] ?? {},
        );
        return { product, doubleTwinPrice };
      })
      .filter((a): a is ProductAlternative => a.doubleTwinPrice.isPresent()),
    (a) => a.doubleTwinPrice.value,
  );
}

export const useCalculationProductAlternatives = (
  selectedProduct: CalculationProduct,
  calculationCurrency: Currency,
  calculationMargin: number,
  products: Pick<ProductResponse, "id" | "name" | "options" | "productsGroup" | "currency">[],
  participantsNumber: number,
): UseCalculationProductAlternativesResult => {
  const alternativeProducts = getAlternativeProducts(selectedProduct, products, calculationCurrency);
  const productIds = [selectedProduct.id, ...alternativeProducts.map((a) => a.id)];
  const { prices } = useProductsSellingPrices(
    productIds.map((id) => ({
      id,
      dateFrom: selectedProduct.date,
      dateTo: dayjs(selectedProduct.date, ISO_DATE_FORMAT).add(1, "day").format(ISO_DATE_FORMAT),
    })),
    participantsNumber,
  );

  const selectedProductDoubleTwinPrice = getSelectedDoubleTwinPrice(
    selectedProduct,
    products,
    prices,
    calculationCurrency,
    calculationMargin,
  );
  const alternatives = getAlternativesWithDoubleTwinPrice(
    alternativeProducts,
    calculationCurrency,
    calculationMargin,
    products,
    prices,
  );

  return {
    alternatives,
    selectedProductDoubleTwinPrice,
  };
};
